'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactCardProps {
  contact: {
    id: string;
    name: string;
    type: string;
    phone: string;
    email?: string;
    address?: string;
    notes?: string;
  };
  onEdit: (contactId: string) => void;
  onDelete: (contactId: string) => void;
  onCall: (phone: string) => void;
  onEmail: (email: string) => void;
}

const ContactCard = ({ contact, onEdit, onDelete, onCall, onEmail }: ContactCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  const [callStatus, setCallStatus] = useState<string | null>(null);

  const getTypeIcon = () => {
    const type = contact.type.toLowerCase();
    if (type.includes('doctor') || type.includes('physician')) return 'UserIcon';
    if (type.includes('insurance')) return 'ShieldCheckIcon';
    if (type.includes('pharmacy')) return 'BeakerIcon';
    if (type.includes('emergency')) return 'ExclamationTriangleIcon';
    return 'PhoneIcon';
  };

  const handleCheckInCall = async () => {
    setIsInitiatingCall(true);
    setCallStatus(null);

    try {
      const response = await fetch('/api/twilio/check-in-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: contact.phone,
          contactName: contact.name,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setCallStatus('Check-in call initiated successfully!');
      } else {
        setCallStatus(data.error?.message || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Error initiating check-in call:', error);
      setCallStatus('Failed to initiate call. Please try again.');
    } finally {
      setIsInitiatingCall(false);
      setTimeout(() => setCallStatus(null), 5000);
    }
  };

  return (
    <div className="glass p-4 hover:shadow-warm-sm transition-smooth">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name={getTypeIcon() as any} size={20} className="text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-caption font-medium text-foreground mb-1 break-words">
              {contact.name}
            </h3>
            <p className="text-sm text-muted-foreground break-words">{contact.type}</p>
          </div>
        </div>
        <div className="relative ml-2">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded hover:bg-muted transition-smooth"
            aria-label="Contact options"
          >
            <Icon
              name="EllipsisVerticalIcon"
              size={20}
              className="text-muted-foreground"
            />
          </button>
          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-8 w-40 bg-popover border border-border rounded-lg shadow-warm-md z-50 py-1">
                <button
                  onClick={() => {
                    onEdit(contact.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2"
                >
                  <Icon name="PencilIcon" size={16} />
                  Edit
                </button>
                <button
                  onClick={() => {
                    onDelete(contact.id);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm font-caption hover:bg-muted transition-smooth flex items-center gap-2 text-error"
                >
                  <Icon name="TrashIcon" size={16} />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={() => onCall(contact.phone)}
            className="flex items-center justify-center sm:justify-start gap-2 text-sm font-caption text-foreground hover:text-primary transition-smooth px-3 py-2 rounded-lg hover:bg-sage-50 flex-1 min-w-0"
          >
            <Icon name="PhoneIcon" size={16} className="text-primary flex-shrink-0" />
            <span className="truncate break-all">{contact.phone}</span>
          </button>
          <button
            onClick={handleCheckInCall}
            disabled={isInitiatingCall}
            className="flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed text-sm font-caption font-medium whitespace-nowrap flex-shrink-0"
            title="Initiate automated check-in call"
          >
            <Icon name="PhoneArrowUpRightIcon" size={16} />
            {isInitiatingCall ? 'Calling...' : 'Check-In'}
          </button>
        </div>
        {contact.email && (
          <button
            onClick={() => onEmail(contact.email!)}
            className="flex items-center gap-2 text-sm font-caption text-foreground hover:text-primary transition-smooth w-full px-3 py-2 rounded-lg hover:bg-sage-50 min-w-0"
          >
            <Icon name="EnvelopeIcon" size={16} className="text-primary flex-shrink-0" />
            <span className="truncate break-all">{contact.email}</span>
          </button>
        )}
      </div>

      {callStatus && (
        <div className={`mt-3 p-2 rounded-lg text-xs font-caption ${
          callStatus.includes('success') ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          {callStatus}
        </div>
      )}

      {(contact.address || contact.notes) && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs font-caption text-primary hover:text-primary/80 transition-smooth mt-3"
          >
            <Icon
              name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={14}
            />
            {expanded ? 'Show less' : 'Show more'}
          </button>
          {expanded && (
            <div className="mt-3 pt-3 border-t border-sage-200/30 space-y-2">
              {contact.address && (
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Address
                  </p>
                  <p className="text-sm text-foreground break-words">{contact.address}</p>
                </div>
              )}
              {contact.notes && (
                <div>
                  <p className="text-xs font-caption text-muted-foreground mb-1">
                    Notes
                  </p>
                  <p className="text-sm text-foreground break-words">{contact.notes}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContactCard;